/*
 * ls-elements
 * Copyright (C) 2024 Libriciel SCOP
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';

@Component({
  selector: 'ls-color-picker',
  standalone: true,
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPickerComponent),
      multi: true
    }
  ],
  imports: [
    FormsModule
  ]
})
export class ColorPickerComponent implements ControlValueAccessor, AfterViewInit {

  @ViewChild('picker') picker!: ElementRef<HTMLDivElement>;    // square with gradient
  @ViewChild('slider') slider!: ElementRef<HTMLDivElement>;    // hue slider
  @ViewChild('pointer') pointer!: ElementRef<HTMLDivElement>;  // color pointer
  @ViewChild('input') input!: ElementRef<HTMLInputElement>;    // input for hex color

  @Input() size: number = 200; // height == width

  // Emit on 'color' property changed
  @Output() changed: EventEmitter<string> = new EventEmitter();

  isDown: boolean = false;     // mouse down on 'slider' element
  sqIsDown: boolean = false;   // mouse down on 'picker' element
  /**
   * The element make an HSV as it's much easier to calculate with a color picker
   * H : hue
   * S : saturation
   * V : value
   */

  hue: number = 0;
  saturation: number = 0;
  value: number = 0;

  // This property is used to calculate 'picker' background with specific hue, we use 'hsl' with current 'hue'
  cssProperties: string = 'linear-gradient(to top, #000000, rgba(255,255,255,0)), linear-gradient(to right, #ffffff , hsl(%hue%, 100%, 50%))';

  constructor(private renderer2: Renderer2, private element: ElementRef) {
  }

  private _color!: string; // Private color, as we need setter and getter to listen to change

  /*******
   * Define color getter and setter
   */
  get color() {
    return this._color;
  }

  @Input()
  set color(value: any) {
    this.defineColor(value);
  }

  /*******
   * Define 'id' of input element and remove id of current element
   */
  ngAfterViewInit(): void {
    const id: string = this.element.nativeElement.getAttribute('id');
    this.renderer2.removeAttribute(this.element.nativeElement, 'id');
    this.renderer2.setAttribute(this.input.nativeElement, 'id', id);
    this.defineColor(this.color);
  }

  /**
   * Update color of 'slider' and 'picker' on 'hue' change
   */
  changePickerColor() {
    if (this.slider) {
      this.renderer2.setStyle(this.slider.nativeElement, 'background', 'hsl(%hue%, 100%, 50%)'.replace('%hue%', '' + this.hue));
      this.renderer2.setStyle(this.picker.nativeElement, 'background', this.cssProperties.replace('%hue%', '' + this.hue));
    }
  }

  /**
   * Update _color property on change, and emit onChange event for ngModel
   */
  pickerChange() {
    const rgb: number[] = this.hsvToRgb();
    const hex: string = '#' + ('00' + rgb[0].toString(16).toUpperCase()).slice(-2) +
      ('00' + rgb[1].toString(16).toUpperCase()).slice(-2) +
      ('00' + rgb[2].toString(16).toUpperCase()).slice(-2);

    this._color = hex;
    this.onChange(hex);
  }

  /**
   * Set 'slider' position with 'hue' value
   */
  huePosition() {
    const px: number = Math.round(Math.max(this.hue * (this.size - 24) / 360, 0));
    if (this.slider) {
      this.renderer2.setStyle(this.slider.nativeElement, 'left', px - 12 + 'px');
    }
    this.changePickerColor();
    this.pickerChange();
  }

  /**
   * Set 'pointer' position with 'saturation' and 'value' values
   */
  sqPosition() {
    if (this.pointer) {
      const px: number = Math.round(Math.max(this.saturation * this.size / 100, 0));
      this.renderer2.setStyle(this.pointer.nativeElement, 'left', px - 12 + 'px');

      const py: number = Math.round(Math.max((100 - this.value) * this.size / 100, 0));
      this.renderer2.setStyle(this.pointer.nativeElement, 'top', py - 12 + 'px');

      this.pickerChange();
    }
  }

  /********
   * When we change 'hue' with mouse click, juste go to the position and emit color change
   * @param event the mouse click event
   */
  changeHue(event: any) {
    this.hue = Math.round(event.offsetX * 360 / event.target.clientWidth);
    this.huePosition();
    this.changed.emit(this._color);
  }

  defineColor(color: any) {
    if (color && color.length === 7) {
      this._color = color;
      const R: number = parseInt(this._color.substring(1, 3), 16);
      const G: number = parseInt(this._color.substring(3, 5), 16);
      const B: number = parseInt(this._color.substring(5), 16);
      this.rgbToHsv(R, G, B);
      this.huePosition();
      this.sqPosition();
      this.changed.emit(this._color);
    }
  }

  /****
   * Events handlers : mouseup, mousemove and mousedown
   */
  @HostListener('document:mouseup', [])
  mouseUp() {
    if (this.isDown || this.sqIsDown) {
      // Trigger event only if it was clicked
      this.changed.emit(this._color);
    }
    this.isDown = false;
    this.sqIsDown = false;
  }

  @HostListener('document:mousemove', ['$event'])
  mouseMove(event: any) {
    if (this.isDown && !!this.slider.nativeElement.parentElement) {
      const rect: DOMRect = this.slider.nativeElement.parentElement.getBoundingClientRect();
      const position: number = event.clientX - rect.left;

      this.hue = Math.round(position * 360 / rect.width);
      if (this.hue > 360) {
        this.hue = 360;
      } else if (this.hue < 0) {
        this.hue = 0;
      }
      this.huePosition();
    }
    if (this.sqIsDown && !!this.pointer.nativeElement.parentElement) {
      const rect: DOMRect = this.pointer.nativeElement.parentElement.getBoundingClientRect();
      const xPosition: number = event.clientX - rect.left;

      this.saturation = Math.round(xPosition * 100 / rect.width);
      if (this.saturation > 100) {
        this.saturation = 100;
      } else if (this.saturation < 0) {
        this.saturation = 0;
      }

      const yPosition: number = event.clientY - rect.top;

      this.value = 100 - Math.round(yPosition * 100 / rect.height);
      if (this.value > 100) {
        this.value = 100;
      } else if (this.value < 0) {
        this.value = 0;
      }

      this.sqPosition();
    }
  }

  hueMouseDown() {
    this.isDown = true;
  }

  sqMouseDown(event: any) {
    this.sqIsDown = true;
    this.mouseMove(event);
  }


  /******
   * ngModel required functions
   */
  onChange: any = () => {
    // tslint:disable-next-line:semicolon
  };

  onTouched: any = () => {
    // tslint:disable-next-line:semicolon
  };

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  writeValue(value: any) {
    this.defineColor(value);
  }


  /*********
   * Utils fonctions, convert HSV <-> RGB
   */
  hsvToRgb() {
    const H: number = this.hue;
    const V: number = this.value / 100;
    const S: number = this.saturation / 100;

    const C: number = V * S;
    const X: number = C * (1 - Math.abs((H / 60) % 2 - 1));
    const m: number = V - C;

    let prime: number[] = [C, 0, X];

    if (H < 60) {
      prime = [C, X, 0];
    } else if (H < 120) {
      prime = [X, C, 0];
    } else if (H < 180) {
      prime = [0, C, X];
    } else if (H < 240) {
      prime = [0, X, C];
    } else if (H < 300) {
      prime = [X, 0, C];
    }

    return [
      Math.round((prime[0] + m) * 255),
      Math.round((prime[1] + m) * 255),
      Math.round((prime[2] + m) * 255)
    ];
  }

  rgbToHsv(R: number, G: number, B: number) {
    const Rp: number = R / 255;
    const Gp: number = G / 255;
    const Bp: number = B / 255;

    const Cmax: number = Math.max(Rp, Gp, Bp);
    const Cmin: number = Math.min(Rp, Gp, Bp);

    const Delta: number = Cmax - Cmin;
    let H: number = 0;

    if (Delta === 0) {
      H = 0;
    } else if (Cmax === Rp) {
      H = 60 * ((Gp - Bp) / Delta + (Gp < Bp ? 6 : 0));
    } else if (Cmax === Gp) {
      H = 60 * ((Bp - Rp) / Delta + 2);
    } else if (Cmax === Bp) {
      H = 60 * ((Rp - Gp) / Delta + 4);
    }

    let S: number = 0;

    if (Cmax !== 0) {
      S = Delta / Cmax;
    }

    this.hue = H;
    this.saturation = S * 100;
    this.value = Cmax * 100;
  }
}
